import React, { useState } from 'react';
import { AlertTriangle, Check, Loader2, CheckCircle2 } from 'lucide-react';

interface ConflictCandidate {
  value: string;
  source_location: string;
  raw_snippet?: string;
}

interface SourceConflict {
  id: number;
  vendorName: string;
  fieldKey: string;
  fieldLabel: string;
  conflictType: string;
  candidates: ConflictCandidate[];
  autoSuggested: string | null;
  chosenValue: string | null;
  resolved: boolean;
}

interface Props {
  conflicts: SourceConflict[];
  resolvingId: number | null;
  onResolve: (conflictId: number, chosenValue: string, chosenSource: string) => void;
}

const TYPE_LABEL: Record<string, string> = {
  NUMERIC: 'Numeric conflict',
  DATE_MISMATCH: 'Date / as-of mismatch',
  AMBIGUOUS_MAPPING: 'Ambiguous source mapping',
};

const ConflictCard: React.FC<{
  conflict: SourceConflict;
  resolving: boolean;
  onResolve: (id: number, value: string, source: string) => void;
}> = ({ conflict, resolving, onResolve }) => {
  const [selected, setSelected] = useState<string>(conflict.autoSuggested || '');
  const [custom, setCustom] = useState<string>('');
  const usingCustom = selected === '__custom__';
  const finalValue = usingCustom ? custom : selected;
  const sourceOf = (val: string) =>
    conflict.candidates.find(c => c.value === val)?.source_location || (usingCustom ? 'Manual override' : '');

  if (conflict.resolved) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
          <span className="font-semibold text-gray-900">{conflict.fieldLabel}</span>
          <span className="text-gray-400">·</span>
          <span className="text-gray-600">{conflict.vendorName}</span>
        </div>
        <div className="mt-1 text-sm text-green-800">
          Resolved to <strong>{conflict.chosenValue}</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border-2 border-amber-300 bg-white p-4">
      <div className="flex items-start gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900">{conflict.fieldLabel}</div>
          <div className="text-xs text-gray-500">
            {conflict.vendorName} · {TYPE_LABEL[conflict.conflictType] || conflict.conflictType}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-600 mb-3">
        The source set gives more than one value for this field. Pick the value to use (or enter your own).
      </p>

      <div className="space-y-2">
        {conflict.candidates.map((c, i) => (
          <label
            key={i}
            className={`flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition-colors ${
              selected === c.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <input
              type="radio"
              name={`conflict-${conflict.id}`}
              checked={selected === c.value}
              onChange={() => setSelected(c.value)}
              className="mt-0.5"
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900">{c.value}</div>
              <div className="text-xs text-gray-500">Source: {c.source_location}</div>
              {c.raw_snippet ? <div className="text-xs text-gray-400 mt-0.5 italic truncate">“{c.raw_snippet}”</div> : null}
            </div>
          </label>
        ))}

        <label
          className={`flex items-center gap-2 rounded-md border p-2.5 cursor-pointer transition-colors ${
            usingCustom ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
          }`}
        >
          <input
            type="radio"
            name={`conflict-${conflict.id}`}
            checked={usingCustom}
            onChange={() => setSelected('__custom__')}
          />
          <span className="text-sm text-gray-700">Other:</span>
          <input
            type="text"
            value={custom}
            onChange={(e) => { setCustom(e.target.value); setSelected('__custom__'); }}
            placeholder="Enter a value"
            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </label>
      </div>

      <div className="mt-3 flex justify-end">
        <button
          onClick={() => onResolve(conflict.id, finalValue, sourceOf(finalValue))}
          disabled={resolving || !finalValue || !finalValue.trim()}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {resolving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Use this value
        </button>
      </div>
    </div>
  );
};

const ConflictResolutionPanel: React.FC<Props> = ({ conflicts, resolvingId, onResolve }) => {
  const open = conflicts.filter(c => !c.resolved);
  const resolved = conflicts.filter(c => c.resolved);

  if (conflicts.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-10 text-center">
        <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
        <p className="text-gray-700 font-medium">No source conflicts</p>
        <p className="text-sm text-gray-500 mt-1">All source values reconcile across the uploaded reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {open.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-gray-900">
              {open.length} unresolved source conflict{open.length !== 1 ? 's' : ''}
            </h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Resolve each conflict so the brief uses the value you intend. The brief is flagged
            as “contains unresolved source conflicts” until these are cleared.
          </p>
          <div className="space-y-3">
            {open.map(c => (
              <ConflictCard key={c.id} conflict={c} resolving={resolvingId === c.id} onResolve={onResolve} />
            ))}
          </div>
        </div>
      )}

      {resolved.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-500 mb-2">Resolved ({resolved.length})</h4>
          <div className="space-y-2">
            {resolved.map(c => (
              <ConflictCard key={c.id} conflict={c} resolving={false} onResolve={onResolve} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConflictResolutionPanel;
